import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { FinancialResults } from '../../models/financial-results';
import { FinancialService } from '../../services/financial.service';

interface FlowSegment {
  label: string;
  value: number;
  percentage: number;
  color: string;
}

interface DailyFlowRow {
  date: string;
  entriesTotal: number;
  outflowsTotal: number;
  result: number;
  entrySegments: FlowSegment[];
  outflowSegments: FlowSegment[];
}

@Component({
  selector: 'app-financial-flows',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, TableModule, DatePipe, DecimalPipe],
  templateUrl: './financial-flows.component.html',
  styleUrls: ['./financial-flows.component.scss'],
})
export class FinancialFlowsComponent implements OnInit {
  rows: DailyFlowRow[] = [];
  periodEntrySegments: FlowSegment[] = [];
  periodOutflowSegments: FlowSegment[] = [];
  periodEntriesTotal = 0;
  periodOutflowsTotal = 0;
  isLoading = false;
  errorMessage = '';
  since!: Date;
  until!: Date;

  constructor(
    private financialService: FinancialService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    const { since, until } = this.getDefaultRange();
    this.applyRange(since, until);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
      const since = params['since'] ? this.parseDate(params['since']) : defaultSince;
      const until = params['until'] ? this.parseDate(params['until']) : defaultUntil;

      this.applyRange(since, until);
      this.fetchResults();
    });
  }

  applyDateFilter(): void {
    const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
    const since = this.since ? this.startOfDay(this.since) : defaultSince;
    const until = this.until ? this.startOfDay(this.until) : defaultUntil;
    this.applyRange(since, until);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
      },
    });
  }

  refresh(): void {
    this.fetchResults();
  }

  fetchResults(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.financialService
      .getFinancialResults({
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
      })
      .subscribe({
        next: (response) => {
          const results = response?.results ?? [];
          const summary = response?.summary;

          this.periodEntrySegments = this.buildEntrySegments({
            date: '',
            cash_in: Number(summary?.cash_in || 0),
            take_rate_cs: Number(summary?.take_rate_cs || 0),
            take_rate_lol: Number(summary?.take_rate_lol || 0),
            battle_pass_income: Number(summary?.battle_pass_income || 0),
            store_income: Number(summary?.store_income || 0),
            extra_missions_income: Number(summary?.extra_missions_income || 0),
            extra_missions_outflow: Number(summary?.extra_missions_outflow || 0),
            cash_out: Number(summary?.cash_out || 0),
            realized_revenue: Number(summary?.realized_revenue || 0),
            net_realized_result: Number(summary?.net_realized_result || 0),
          });
          this.periodOutflowSegments = this.buildOutflowSegments({
            date: '',
            cash_in: 0,
            take_rate_cs: 0,
            take_rate_lol: 0,
            battle_pass_income: 0,
            store_income: 0,
            extra_missions_income: 0,
            extra_missions_outflow: Number(summary?.extra_missions_outflow || 0),
            cash_out: Number(summary?.cash_out || 0),
            realized_revenue: 0,
            net_realized_result: 0,
          });
          this.periodEntriesTotal = this.sumSegments(this.periodEntrySegments);
          this.periodOutflowsTotal = this.sumSegments(this.periodOutflowSegments);
          this.rows = results.map((row) => this.buildDailyFlowRow(row));
          this.isLoading = false;
        },
        error: () => {
          this.rows = [];
          this.periodEntrySegments = [];
          this.periodOutflowSegments = [];
          this.periodEntriesTotal = 0;
          this.periodOutflowsTotal = 0;
          this.errorMessage = 'Não foi possível carregar entradas e saídas.';
          this.isLoading = false;
        },
      });
  }

  trackSegment(index: number, segment: FlowSegment): string {
    return `${index}-${segment.label}`;
  }

  private buildDailyFlowRow(row: FinancialResults): DailyFlowRow {
    const normalizedRow = this.normalizeFinancialRow(row);
    const entrySegments = this.buildEntrySegments(normalizedRow);
    const outflowSegments = this.buildOutflowSegments(normalizedRow);

    return {
      date: normalizedRow.date,
      entriesTotal: this.sumSegments(entrySegments),
      outflowsTotal: this.sumSegments(outflowSegments),
      result: normalizedRow.net_realized_result,
      entrySegments,
      outflowSegments,
    };
  }

  private normalizeFinancialRow(row: FinancialResults): FinancialResults {
    return {
      ...row,
      cash_in: Number(row.cash_in || 0),
      take_rate_cs: Number(row.take_rate_cs || 0),
      take_rate_lol: Number(row.take_rate_lol || 0),
      battle_pass_income: Number(row.battle_pass_income || 0),
      store_income: Number(row.store_income || 0),
      extra_missions_income: Number(row.extra_missions_income || 0),
      extra_missions_outflow: Number(row.extra_missions_outflow || 0),
      cash_out: Number(row.cash_out || 0),
      realized_revenue: Number(row.realized_revenue || 0),
      net_realized_result: Number(row.net_realized_result || 0),
    };
  }

  private buildEntrySegments(row: FinancialResults): FlowSegment[] {
    return this.withPercentages([
      { label: 'Compra de moedas', value: row.cash_in, percentage: 0, color: '#38bdf8' },
      { label: 'Take Rate CS', value: row.take_rate_cs, percentage: 0, color: '#22c55e' },
      { label: 'Take Rate LoL', value: row.take_rate_lol, percentage: 0, color: '#84cc16' },
      { label: 'Passe', value: row.battle_pass_income, percentage: 0, color: '#a855f7' },
      { label: 'Loja', value: row.store_income, percentage: 0, color: '#f59e0b' },
      { label: 'Missões extras', value: row.extra_missions_income, percentage: 0, color: '#ec4899' },
    ]);
  }

  private buildOutflowSegments(row: FinancialResults): FlowSegment[] {
    return this.withPercentages([
      { label: 'Saques pagos', value: row.cash_out, percentage: 0, color: '#ef4444' },
      { label: 'Saídas missões extras', value: row.extra_missions_outflow, percentage: 0, color: '#f97316' },
    ]);
  }

  private withPercentages(segments: FlowSegment[]): FlowSegment[] {
    const total = this.sumSegments(segments);
    return segments.map((segment) => ({
      ...segment,
      percentage: total > 0 ? (segment.value / total) * 100 : 0,
    }));
  }

  private sumSegments(segments: FlowSegment[]): number {
    return segments.reduce((total, segment) => total + Number(segment.value || 0), 0);
  }

  private getDefaultRange(): { since: Date; until: Date } {
    const until = this.startOfDay(new Date());
    const since = this.addDays(until, -29);
    return { since, until };
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return this.startOfDay(result);
  }

  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return this.startOfDay(new Date());
    }
    return new Date(year, month - 1, day);
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private applyRange(since: Date, until: Date): void {
    const normalizedSince = this.startOfDay(since);
    const normalizedUntil = this.startOfDay(until);
    this.since = normalizedSince <= normalizedUntil ? normalizedSince : normalizedUntil;
    this.until = normalizedSince <= normalizedUntil ? normalizedUntil : normalizedSince;
  }
}
